import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { User } from '@/types';
import { api, clearApiAuthToken, setApiAuthToken, ExtendedRequestConfig } from '@/services/api';
import { useThemeStore } from '@/stores/theme';

const CACHE_DURATION_MS = 10 * 60 * 60 * 1000; // 10 horas

let hasHydratedFlag = false;
const hydrationCallbacks: (() => void)[] = [];

function syncUserTheme(user: User) {
  if (user.themePreference) {
    useThemeStore.getState().syncTheme(user.themePreference);
  }
}

export function onAuthHydrated(callback: () => void) {
  if (hasHydratedFlag) {
    callback();
    return () => {};
  }
  hydrationCallbacks.push(callback);
  return () => {
    const idx = hydrationCallbacks.indexOf(callback);
    if (idx !== -1) hydrationCallbacks.splice(idx, 1);
  };
}

interface AuthState {
  user: User | null;
  accessToken: string | null;
  isAuthenticated: boolean;
  isInitialized: boolean;
  isLoading: boolean;
  error: string | null;
  expiresAt: number | null;

  // Actions
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  initializeAuth: () => Promise<void>;
  clearError: () => void;
  setUser: (user: User) => void;
}

interface LoginResponse {
  user: User;
  accessToken: string;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      accessToken: null,
      isAuthenticated: false,
      isInitialized: false,
      isLoading: false,
      error: null,
      expiresAt: null,

      login: async (email: string, password: string) => {
        set({ isLoading: true, error: null });

        try {
          const response = await api.post<{ success: boolean; data: LoginResponse }>('/auth/login', {
            email,
            password,
          });

          const { user, accessToken } = response.data;
          setApiAuthToken(accessToken);

          const expiresAt = Date.now() + CACHE_DURATION_MS;

          set({
            user,
            accessToken,
            isAuthenticated: true,
            isInitialized: true,
            isLoading: false,
            error: null,
            expiresAt,
          });

          syncUserTheme(user);
        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : 'Erro ao fazer login';
          clearApiAuthToken();
          set({
            user: null,
            accessToken: null,
            isLoading: false,
            isAuthenticated: false,
            error: errorMessage,
            expiresAt: null,
          });
          throw error;
        }
      },

      logout: async () => {
        try {
          await api.post('/auth/logout');
        } catch {
          // Ignore logout network errors and always clear local auth state.
        }

        clearApiAuthToken();
        set({
          user: null,
          accessToken: null,
          isAuthenticated: false,
          isInitialized: true,
          error: null,
          expiresAt: null,
        });
      },

      initializeAuth: async () => {
        if (get().isInitialized) {
          return;
        }

        set({ isLoading: true, error: null });

        let { accessToken, expiresAt } = get();

        // Fallback: se o Zustand ainda não reidratou, lê direto do localStorage
        if (!accessToken) {
          try {
            const raw = localStorage.getItem('auth-storage');
            if (raw) {
              const parsed = JSON.parse(raw);
              if (parsed?.state?.accessToken) {
                accessToken = parsed.state.accessToken as string;
                expiresAt = parsed.state.expiresAt as number | null;
              }
            }
          } catch {
            // ignore parse errors
          }
        }

        // Se temos um token em cache e ele ainda não expirou, restaura a sessão imediatamente
        if (accessToken && expiresAt && Date.now() < expiresAt) {
          setApiAuthToken(accessToken);
          set({
            accessToken,
            expiresAt,
            isAuthenticated: true,
            isInitialized: true,
            isLoading: false,
          });

          // Tenta renovar o token em background silenciosamente
          try {
            const response = await api.post<{ success: boolean; data: LoginResponse }>('/auth/refresh', undefined, { skipAuthRefresh: true } as ExtendedRequestConfig);
            const { user, accessToken: newAccessToken } = response.data;

            setApiAuthToken(newAccessToken);
            set({
              user,
              accessToken: newAccessToken,
              expiresAt: Date.now() + CACHE_DURATION_MS,
            });

            syncUserTheme(user);
          } catch {
            // Ignora erro de refresh em background; o token atual ainda pode ser válido
          }
          return;
        }

        try {
          const response = await api.post<{ success: boolean; data: LoginResponse }>('/auth/refresh', undefined, { skipAuthRefresh: true } as ExtendedRequestConfig);
          const { user, accessToken: newAccessToken } = response.data;

          setApiAuthToken(newAccessToken);
          set({
            user,
            accessToken: newAccessToken,
            isAuthenticated: true,
            isInitialized: true,
            isLoading: false,
            error: null,
            expiresAt: Date.now() + CACHE_DURATION_MS,
          });

          syncUserTheme(user);
        } catch {
          clearApiAuthToken();
          set({
            user: null,
            accessToken: null,
            isAuthenticated: false,
            isInitialized: true,
            isLoading: false,
            expiresAt: null,
          });
        }
      },

      clearError: () => {
        set({ error: null });
      },

      setUser: (user: User) => {
        set({ user });
      },
    }),
    {
      name: 'auth-storage',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        user: state.user,
        accessToken: state.accessToken,
        isAuthenticated: state.isAuthenticated,
        expiresAt: state.expiresAt,
      }),
      onRehydrateStorage: () => (state) => {
        hasHydratedFlag = true;
        hydrationCallbacks.forEach((cb) => cb());

        if (state?.accessToken) {
          if (state.expiresAt && Date.now() > state.expiresAt) {
            clearApiAuthToken();
          } else {
            setApiAuthToken(state.accessToken);
          }
        }
      },
    }
  )
);
